/**
 * Anchor cache per `04-` §16.5 + D-19.
 *
 * Refcounted begin/end lifecycle with try/finally cleanup. Snapshots
 * pre-mutation summary bboxes at the START of an adapter-driven mutation
 * window (drag, lightbox save, inline edit) so the engine receives
 * `Snapshot.anchorOverrides[summaryId] = preDragBbox` even though gantt
 * has already auto-aggregated the summary's live dates from the moved
 * subtask.
 *
 * Per D-19 lifecycle:
 *   - `beginWindow()`: increments refcount. On first increment (1), reads
 *     the current bbox of every summary into the snapshot map.
 *   - `endWindow()`: decrements refcount. On last decrement (0), clears.
 *   - `cancelWindow()`: explicit cleanup when a drag/lightbox cancel fires
 *     before the matched endWindow could run (forces refcount to 0 +
 *     clears, regardless of nesting).
 *
 * Each adapter mutating entry wraps its body in:
 *   anchorCache.beginWindow(gantt);
 *   try { ... } finally { anchorCache.endWindow(gantt); }
 *
 * Exceptions during the body trigger `finally` so the window never leaks.
 *
 * Multi-gantt-instance safe: state is keyed by gantt-instance reference
 * via WeakMap. Phase B's RevisionTracker uses the same pattern.
 */

import type { TaskID, AnchorOverride } from "../core/types";

/**
 * Per-instance anchor cache state.
 */
interface CacheState {
  refcount: number;
  bboxes: Map<TaskID, AnchorOverride>;
}

/**
 * Structural subset of gantt the anchor cache reads.
 */
export interface GanttAnchorBackend {
  eachTask: (cb: (task: { id: TaskID; type?: string; start_date?: Date; end_date?: Date }) => void) => void;
  config: { types?: { project?: string } };
}

const stateByGantt = new WeakMap<object, CacheState>();

function getState(gantt: GanttAnchorBackend): CacheState {
  let s = stateByGantt.get(gantt as unknown as object);
  if (!s) {
    s = { refcount: 0, bboxes: new Map() };
    stateByGantt.set(gantt as unknown as object, s);
  }
  return s;
}

/**
 * Capture pre-mutation summary bboxes. Reads every task whose type matches
 * `gantt.config.types.project` and clones its current start/end into the
 * bbox map. Idempotent within a single window — first call captures, later
 * begins (under refcount > 1) leave the capture intact.
 */
function captureSnapshot(gantt: GanttAnchorBackend, state: CacheState): void {
  state.bboxes.clear();
  const projectType = gantt.config?.types?.project ?? "project";
  gantt.eachTask((t) => {
    if (t.type !== projectType) return;
    if ((t as any).$virtual) return;
    if (!t.start_date || !t.end_date) return;
    state.bboxes.set(t.id, {
      start: new Date(t.start_date.getTime()),
      end: new Date(t.end_date.getTime())
    });
  });
}

/**
 * Open an anchor-cache window. Captures pre-mutation summary bboxes on
 * first nested entry; subsequent entries just bump the refcount.
 */
export function beginWindow(gantt: GanttAnchorBackend): void {
  const state = getState(gantt);
  state.refcount++;
  if (state.refcount === 1) {
    captureSnapshot(gantt, state);
  }
}

/**
 * Close an anchor-cache window. Decrements refcount; on last close
 * clears the captured bboxes so the next window captures fresh state.
 *
 * Defensive: refcount never goes below 0. A spurious endWindow without a
 * matching beginWindow is a no-op (the prior begin would have been the
 * cancelled mutation; subsequent ends were already flushed).
 */
export function endWindow(gantt: GanttAnchorBackend): void {
  const state = getState(gantt);
  if (state.refcount === 0) return;
  state.refcount--;
  if (state.refcount === 0) {
    state.bboxes.clear();
  }
}

/**
 * Forcibly close all open windows. Used by cancellation hooks
 * (`onTaskDragCancel`, lightbox cancel, inline-edit blur-without-save).
 * Wipes refcount + bboxes regardless of nesting depth.
 */
export function cancelWindow(gantt: GanttAnchorBackend): void {
  const state = getState(gantt);
  state.refcount = 0;
  state.bboxes.clear();
}

/**
 * Read the current anchor override map. Returns an empty Map when no
 * window is open (the engine then treats `TaskSnapshot.startDate / endDate`
 * as the anchor — the "fresh state" case).
 *
 * Per D-19: anchor overrides are populated ONLY for summaries whose live
 * bbox already differs from the pre-mutation bbox. Adapter caller
 * (snapshot_builder.ts) consults gantt's current state vs. our capture to
 * decide which overrides to surface.
 */
export function getOverridesFor(gantt: GanttAnchorBackend): ReadonlyMap<TaskID, AnchorOverride> {
  const state = getState(gantt);
  // Phase C Day 9 returns the raw captured bboxes; the rule "include override
  // only if T.id !== draggedTaskId && live bbox differs" is enforced in
  // `snapshot_builder.ts` which has access to draggedTaskIds and the live
  // bboxes side-by-side. Returning the full capture here keeps `anchor_cache`
  // purely about state capture; the override-filtering decision lives at the
  // boundary where both inputs are available.
  return state.bboxes;
}

/**
 * Test-only accessor for asserting refcount during nesting/cancel tests.
 * Not part of the public adapter contract — exported with an underscore
 * prefix as a convention.
 */
export function _refcountFor(gantt: GanttAnchorBackend): number {
  return getState(gantt).refcount;
}
